import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { SharedFields, teamMembersSectionConfig } from "../../configs";
import {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import {
  commentsField,
  decisionsField,
  expectedImpactField,
  projectTeamMembersField,
  sourceOfBudgetField,
  targetAudienceField,
} from "../tp-screen/tp-screen-config";

const weightField: ScreenSectionConfigType = {
  field: "workload",
  label: "activities.workload",
  view: "normal",
};

const ntkpiField: ScreenSectionConfigType = {
  field: "ntKpi",
  label: "ntkpi.ntkpi",
  view: "normal",
  format: (data) => {
    const ntkpi = data["ntKpi"];
    return AdaaHelper.getItemValueByToken(ntkpi, "name");
  },
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const newVal = $newValue as { id: number };
    const oldVal = $oldValue as { id: number };
    return newVal?.id !== oldVal?.id;
  },
};

const projectName: ScreenSectionConfigType = {
  field: "entityType",
  label: "data_entry.project_name",
  view: "normal",
  format: (data) => AdaaHelper.getItemValueByToken(data, "projectName"),
};

const contributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "kpi.contributing_entities",
  view: "list",
  idKey: "entityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => !AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "entityName"),
  visibleIf: (data: { type: number }) => data?.type === Constants.PROJECT_TYPES_DS3.M1,
};

const otherContributingEntitiesField: ListSectionConfigType = {
  field: "contributingEntities",
  label: "other_entities.contrib_entities",
  view: "list",
  idKey: "otherEntityId",
  derivedList: (data: unknown) => {
    const list = data as { otherEntityId?: number }[];
    return list.filter(($i) => AdaaHelper.isDefined($i?.otherEntityId));
  },
  format: ($o) => AdaaHelper.getItemValueByToken($o, "otherEntityName"),
  visibleIf: (data: { type: number }) => data?.type === Constants.PROJECT_TYPES_DS3.M1,
};

const teamMembersField: AccordionSectionConfigType = {
  view: "accordion",
  field: "teamMembers",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: teamMembersSectionConfig,
  label: "national_projects.work_team_members",
};

const targetsField: ScreenSectionConfigType = {
  field: "targets",
  label: "data_entry.show_targets",
  view: "targets",
  sectionTitle: "activities.targets",
};

const cofogCategoryField: ScreenSectionConfigType = {
  field: "cofogCategory",
  label: "activities.cofog_category",
  view: "normal",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "cofogCategory"),
};

const otherCategoryField: ScreenSectionConfigType = {
  field: "otherCategory",
  label: "activities.other_category",
  view: "normal",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "otherCategory"),
};

const taskSectionConfig = [
  ...SharedFields.name,
  ...SharedFields.description,
  SharedFields.startDate,
  SharedFields.endDate,
  teamMembersField,
];

const tasksField: AccordionSectionConfigType = {
  view: "accordion",
  field: "tasks",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: taskSectionConfig,
  label: "tasks.tasks",
};

const mainAchievemntsField: ListSectionConfigType = {
  field: "comments",
  label: "national_projects.comments",
  view: "list",
  idKey: "itemId",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "comments"),
};

export const activitiesSectionConfig = [
  ...SharedFields.name,
  ...SharedFields.description,
  {
    ...SharedFields.startDate,
    label: "national_projects.planned_start_date",
  },
  {
    ...SharedFields.endDate,
    label: "national_projects.planned_end_date",
  },
  weightField,
  SharedFields.nationalSectorsField,
  contributingEntitiesField,
  otherContributingEntitiesField,
  SharedFields.orgUnitIdField,
  SharedFields.ownerField,
  cofogCategoryField,
  otherCategoryField,
  teamMembersField,
  targetsField,
];

export const activitiesField: AccordionSectionConfigType = {
  view: "accordion",
  field: "activities",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: activitiesSectionConfig,
  label: "activities.title",
  derivedLabel: ($o) =>
    $o.type === Constants.PROJECT_TYPES_DS3.M1 ? "national_projects.milestones" : "activities.title",
};

export const activitiesScreenConfig = {
  type: "simple" as ScreenType,
  sections: [
    ...SharedFields.name,
    ...SharedFields.description,
    projectName,
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    {
      ...SharedFields.startDate,
      field: "actualStartDate",
      label: "national_projects.actual_start_date",
      format: (data: { actualStartDate: number }) => {
        if (!AdaaHelper.isDefined(data?.actualStartDate)) return "";
        return AdaaHelper.formatDate(data?.actualStartDate, false);
      },
    },
    {
      ...SharedFields.endDate,
      field: "actualEndDate",
      label: "national_projects.actual_end_date",
      format: (data: { actualEndDate: number }) => {
        if (!AdaaHelper.isDefined(data?.actualEndDate)) return "";
        return AdaaHelper.formatDate(data?.actualEndDate, false);
      },
    },
    SharedFields.nationalSectorsField,
    SharedFields.orgUnitIdField,
    SharedFields.ownerField,
    cofogCategoryField,
    otherCategoryField,
    mainAchievemntsField,
    teamMembersField,
    tasksField,
    SharedFields.attachmentsField,
    targetsField,
  ],
};

export const linkedNationalStrategiesField: ScreenSectionConfigType = {
  view: "linked-national-strategies",
  field: "govDirections",
  label: "national_strategies.strategies",
};

export const newProjectsScreenConfig = {
  type: "simple" as ScreenType,
  sections: [
    ...SharedFields.name,
    ...SharedFields.description,
    {
      ...SharedFields.objectiveIdField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "objectiveName"),
    },
    {
      ...SharedFields.startDate,
      label: "national_projects.planned_start_date",
    },
    {
      ...SharedFields.endDate,
      label: "national_projects.planned_end_date",
    },
    {
      ...SharedFields.startDate,
      field: "actualStartDate",
      label: "national_projects.actual_start_date",
      format: (data: { actualStartDate: number }) => {
        if (!AdaaHelper.isDefined(data?.actualStartDate)) return "";
        return AdaaHelper.formatDate(data?.actualStartDate, false);
      },
    },
    {
      ...SharedFields.endDate,
      field: "actualEndDate",
      label: "national_projects.actual_end_date",
      format: (data: { actualEndDate: number }) => {
        if (!AdaaHelper.isDefined(data?.actualEndDate)) return "";
        return AdaaHelper.formatDate(data?.actualEndDate, false);
      },
    },
    {
      ...SharedFields.entityIdField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "entityName"),
      label: "national_projects.accountable_entity",
    },
    {
      ...SharedFields.ownerField,
      label: "national_projects.entity_lead",
      field: "entityLead",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "entityLeadName"),
    },
    {
      ...SharedFields.ownerField,
      field: "projectManager",
      label: "national_projects.project_coordinator",
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "projectManagerName"),
    },
    SharedFields.orgUnitIdField,
    {
      ...SharedFields.ownerField,
      format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "ownerManagerName"),
    },
    sourceOfBudgetField,
    contributingEntitiesField,
    otherContributingEntitiesField,
    ntkpiField,
    SharedFields.nationalSectorsField,
    {
      ...decisionsField,
      visibleIf: (data: { type: number }) => data?.type === Constants.PROJECT_TYPES_DS3.M1,
    },
    targetAudienceField,
    expectedImpactField,
    commentsField,
    linkedNationalStrategiesField,
    projectTeamMembersField,
    activitiesField,
    SharedFields.attachmentsField,
  ],
};
